import {useAppSelector} from '../../../hooks/react-redux'
import useMyId from '../../../hooks/useMyId'
import {usePermission} from '../../../hooks/usePermission'
import {getBuild, getIsStarred, getProjectIdFromBuildId} from '../../../selectors'
import {Permission, ROOT_PROJECT_ID} from '../../../types'
import type {BuildId} from '../../../types'
import {useIsCancellable} from '../StopBuild/StopBuild.hooks'

import {BuildBatchAction} from './Builds.utils'

export function useAvailableActions(
  buildId?: BuildId | null,
): Record<BuildBatchAction, boolean | undefined> {
  const projectId = useAppSelector(state => getProjectIdFromBuildId(state, buildId))
  const canTag = usePermission(Permission.TAG_BUILD, projectId)
  const canChangeOwnProfile = usePermission(Permission.CHANGE_OWN_PROFILE, ROOT_PROJECT_ID)
  const isStarred = useAppSelector(state => getIsStarred(state, buildId))
  const canPin = usePermission(Permission.PIN_UNPIN_BUILD, projectId)
  const buildState = useAppSelector(state => getBuild(state, buildId)?.state)
  const isPinned = useAppSelector(state => getBuild(state, buildId)?.pinned)
  const canCancel = useIsCancellable(buildId)
  const canRemove = usePermission(Permission.REMOVE_BUILD, projectId)
  const myId = useMyId()
  const isMyPersonalBuild = useAppSelector(state => {
    const {personal, user} = getBuild(state, buildId) || {}
    return personal && user?.id != null && user.id === myId
  })
  return {
    [BuildBatchAction.ADD_TAGS]: canTag,
    [BuildBatchAction.ADD_TO_FAVORITES]: canTag && canChangeOwnProfile && !isStarred,
    [BuildBatchAction.COMMENT]: usePermission(Permission.COMMENT_BUILD, projectId),
    [BuildBatchAction.PIN]: canPin && buildState === 'finished' && !isPinned,
    [BuildBatchAction.UNPIN]: canPin && buildState === 'finished' && isPinned,
    [BuildBatchAction.REMOVE_FROM_QUEUE]: canCancel && buildState === 'queued',
    [BuildBatchAction.STOP]: canCancel && buildState === 'running',
    [BuildBatchAction.REMOVE]: (canRemove || isMyPersonalBuild) && buildState === 'finished',
  }
}

export function useHasAvailableActions(buildId?: BuildId | null): boolean {
  const availableActions = useAvailableActions(buildId)
  return Object.values(availableActions).some(Boolean)
}
